import * as React from "react";
import * as S from "./style";
import ChatBox from "./ChatBox";
import { ChatBoxType, SystemPatientResults } from "./ChatBox";
import { GuidelineDocWithChunks } from "./GuidelineSources/types";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";
import WaveText from "./WaveText";
import ChatInput from "./ChatInput";
import { isSnapshotActive } from "../../common/services/stream";
import { useChatStreamProvider, useConversationStream } from "../../contexts/ChatStreamContext";
import { createMessage, listMessages, Message as StoredMessage } from "./requests";
import MarkdownContent from "./MarkdownContent";
import { semantic, hexToRGBA } from "../../common/theme/brandTokens";
import { useLanguage } from "../../contexts/LanguageContext";
import SummaryLoading from "./SummaryLoading";
import ThinkingIndicator from "./ThinkingIndicator";
import { useTypewriter } from "./Typewriter/useTypewriter";
import { normalizeListMarkers } from "./normalizeListMarkers";

// Progressively reveals streamed text one character at a time (typing effect)
// while still rendering it as markdown, so formatting matches the finalized
// message. Used only for the actively-streaming entry; once the stream ends the
// entry falls back to a plain <MarkdownContent /> showing the full response.
function TypewriterMarkdown({ text, cps = 120 }: { text: string; cps?: number }) {
  const shown = useTypewriter(text, cps);
  return <MarkdownContent content={shown} />;
}

const defaultValue: ChatBoxType = {
  role: "system",
  content:
    "You are a helpful AI assistant that is answering questions from a database.",
  reference_ids: {},
};
export const SEMANTIC_SEARCH_KEY = "semantic-search";

// Stable client_id for the single live (streaming/just-finished) entry appended
// to the viewed conversation's transcript.
const LIVE_ID = "__live_stream__";

interface ChatProps {
  meta?: any;
  patientId?: string;
  modelRunId?: string | null;
  copilotModel?: string | null;
  conversationId?: string | null;
  pendingConversationId?: string | null;
  initialCopilotResponse?: string | null;
  isTranslatingCopilot?: boolean;
  copilotResponseMeta?: { run_id: string; created_at: string; tft_model?: string | null; copilot_model?: string | null } | null;
  onCreateConversation?: () => Promise<any>;
  onConversationsChange?: (conversations: any[]) => void;
  onConversationChange?: (conversationId: string) => void;
  onConversationSummary?: (conversationId: string, summary: string) => void;
  /** True when the viewed conversation is shared with the current user (not owned) — locks the composer. */
  readOnly?: boolean;
  /** The conversation's owner, shown as a "Shared by" badge when it isn't the current user. */
  owner?: { name?: string | null; email?: string | null; avatarUrl?: string | null } | null;
}

export default function Chat(props: ChatProps) {
  const { t } = useLanguage();
  const chatRef = React.useRef<HTMLDivElement>(null);
  const chatContainerRef = React.useRef<HTMLDivElement>(null);
  const conversationsChangeRef = React.useRef(props.onConversationsChange);
  const conversationChangeRef = React.useRef(props.onConversationChange);
  const conversationSummaryRef = React.useRef(props.onConversationSummary);
  const [error, setError] = React.useState<string>("");
  const [selectedUuid, setSelectedUuid] = React.useState<string>(props.conversationId ?? "");
  // The persisted messages of the *currently viewed* conversation (no seed row).
  // Reset whenever the viewed conversation changes; reloaded by the fetch effect.
  const [fetchedHistory, setFetchedHistory] = React.useState<ChatBoxType[]>([]);
  const [historyRefreshKey, setHistoryRefreshKey] = React.useState(0);
  const derivedPatientId = React.useMemo(() => {
    if (props.patientId) return props.patientId;
    if (props.meta?.patient_id) return String(props.meta.patient_id);
    if (props.meta?.subject_id) return String(props.meta.subject_id);
    return undefined;
  }, [props.patientId, props.meta]);

  // Live stream snapshot for the conversation currently being viewed. Owned by
  // the app-level ChatStreamProvider (above the router), so it survives Chat
  // unmounting on navigation and is always scoped to `selectedUuid` — a stream
  // for another conversation can never leak into this view.
  const provider = useChatStreamProvider();
  const stream = useConversationStream(selectedUuid);
  const streamActive = isSnapshotActive(stream);
  // Busy for the whole generation lifecycle (thinking → streaming) — drives the
  // composer lock so no second message can be sent mid-generation.
  const isBusy = streamActive;

  React.useEffect(() => {
    conversationsChangeRef.current = props.onConversationsChange;
  }, [props.onConversationsChange]);

  React.useEffect(() => {
    conversationChangeRef.current = props.onConversationChange;
  }, [props.onConversationChange]);

  React.useEffect(() => {
    conversationSummaryRef.current = props.onConversationSummary;
  }, [props.onConversationSummary]);

  const meta = props.meta;

  // Route a late `summary` (title update) for the viewed conversation to the parent.
  React.useEffect(() => {
    if (!selectedUuid) return;
    provider.setSummaryHandler(selectedUuid, (summary) =>
      conversationSummaryRef.current?.(selectedUuid, summary)
    );
  }, [selectedUuid, provider]);

  React.useEffect(() => {
    if (stream.error) setError(stream.error);
  }, [stream.error]);

  // Notify the parent which conversation is being viewed.
  React.useEffect(() => {
    conversationChangeRef.current?.(selectedUuid);
  }, [selectedUuid]);

  // Sync the viewed conversation from props. Changing it resets the transcript;
  // the fetch effect below reloads the target's messages. Because the live entry
  // is derived purely from the provider snapshot for `selectedUuid` (see the
  // `textHistory` memo), switching conversations cannot show another's messages.
  React.useEffect(() => {
    const target = props.conversationId ?? "";
    if (target === selectedUuid) return;
    setSelectedUuid(target);
    setFetchedHistory([]);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.conversationId]);

  // When the viewed conversation's stream ends, refetch so the persisted answer
  // (the server writes it before sending `end`) replaces the live entry. A short
  // delayed retry covers the user-stop case, where the partial persist can race.
  React.useEffect(() => {
    if (!stream.hasEnded) return;
    setHistoryRefreshKey((k) => k + 1);
    const id = window.setTimeout(() => setHistoryRefreshKey((k) => k + 1), 1500);
    return () => window.clearTimeout(id);
  }, [stream.hasEnded]);

  // Load the viewed conversation's persisted messages.
  React.useEffect(() => {
    if (!selectedUuid) {
      setFetchedHistory([]);
      return;
    }
    // A temp id for a not-yet-created conversation — nothing to fetch.
    if (props.pendingConversationId && props.pendingConversationId === selectedUuid) return;

    let cancelled = false;
    listMessages(selectedUuid)
      .then((res) => {
        if (cancelled) return;
        const rows: StoredMessage[] = Array.isArray(res.data) ? res.data : [];
        const mapped: ChatBoxType[] = rows.map((msg) => {
          const normalizedRole: ChatBoxType["role"] =
            msg.role === "assistant" || msg.role === "system" ? "system" : "user";
          const guidelineDocs: GuidelineDocWithChunks[] | undefined = Array.isArray(msg.guideline_docs)
            ? msg.guideline_docs.map((doc) => ({
                guideline_docs_id: doc.document_id,
                image_url: doc.image_url,
              }))
            : undefined;
          return {
            role: normalizedRole,
            content: msg.content,
            reference_ids: {},
            messageId: msg.message_id,
            guidelineDocs: guidelineDocs && guidelineDocs.length > 0 ? guidelineDocs : undefined,
            guidelineChunkIds: Array.isArray(msg.guideline_chunks) ? msg.guideline_chunks : undefined,
            memoryFacts: Array.isArray(msg.memory_facts) && msg.memory_facts.length > 0 ? msg.memory_facts : undefined,
            createdAt: msg.created_at,
          };
        });
        // Don't blank an in-flight turn: if the fetch comes back empty (read-
        // after-write race on a brand-new conversation) while a stream is
        // active/just-ended, keep what we already show and let the live entry /
        // a later refetch fill in. Otherwise the fetched rows are the source of
        // truth for this conversation.
        setFetchedHistory((prev) => {
          if (
            mapped.length === 0 &&
            prev.length > 0 &&
            (isSnapshotActive(stream) || stream.hasEnded)
          ) {
            return prev;
          }
          return mapped;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch message history:", err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUuid, props.pendingConversationId, historyRefreshKey]);

  // The rendered transcript: seed + this conversation's persisted messages, plus
  // (while it is generating, or just finished before the refetch lands) a single
  // live entry sourced from the provider snapshot for THIS conversation.
  const textHistory = React.useMemo<ChatBoxType[]>(() => {
    const list: ChatBoxType[] = [defaultValue, ...fetchedHistory];
    const liveText = stream.text || "";
    const showFinalized =
      !streamActive &&
      stream.hasEnded &&
      liveText.trim().length > 0 &&
      !fetchedHistory.some(
        (m) => m.role === "system" && (m.content || "").trim() === liveText.trim()
      );
    if (streamActive || showFinalized) {
      list.push({
        role: "system",
        content: liveText,
        reference_ids: {},
        client_id: LIVE_ID,
        guidelines:
          stream.guidelines && stream.guidelines.length > 0 ? stream.guidelines : undefined,
        memoryFacts:
          stream.memoryFacts && stream.memoryFacts.length > 0 ? stream.memoryFacts : undefined,
      });
    }
    return list;
  }, [fetchedHistory, stream, streamActive]);

  React.useEffect(() => {
    const node = chatContainerRef.current;
    if (node) {
      requestAnimationFrame(() => {
        node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
      });
    }
  }, [textHistory]);

  React.useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [textHistory, selectedUuid]);

  // Jump-to-source for memory-fact citations: MemorySources navigates to
  // /c/<shortcode>#msg-<message_id>; once the target conversation's history has
  // rendered, scroll that message into view and briefly highlight it. Retries a
  // few times because the fetch that populates fetchedHistory is async, and also
  // re-runs on hashchange (clicking a source within the same conversation).
  React.useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash.startsWith("#msg-")) return;
      const id = hash.slice(1);
      let tries = 0;
      const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Highlight just the message text, not the whole row: fall back to the
          // row only if the content element isn't found.
          const target = (el.querySelector(".chatbox-highlight-target") as HTMLElement) ?? el;
          const prev = target.style.backgroundColor;
          const prevRadius = target.style.borderRadius;
          target.style.transition = "background-color 0.4s ease";
          target.style.backgroundColor = hexToRGBA(semantic.warning.main, 0.25);
          target.style.borderRadius = "4px";
          window.setTimeout(() => {
            target.style.backgroundColor = prev;
            target.style.borderRadius = prevRadius;
          }, 1600);
        } else if (tries++ < 10) {
          window.setTimeout(attempt, 250);
        }
      };
      attempt();
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, [fetchedHistory, selectedUuid]);

  return (
    <S.Main>
      <S.ChatWrapper id="chat-wrapper" ref={chatContainerRef}>
        {props.isTranslatingCopilot ? (
          <S.CopilotSkeletonWrapper>
            <SummaryLoading />
          </S.CopilotSkeletonWrapper>
        ) : props.initialCopilotResponse && (
          <SystemPatientResults
            meta={meta}
            text={props.initialCopilotResponse}
            runId={props.copilotResponseMeta?.run_id}
            createdAt={props.copilotResponseMeta?.created_at}
            tftModel={props.copilotResponseMeta?.tft_model}
            copilotModel={props.copilotResponseMeta?.copilot_model}
          />
        )}
        {textHistory
          .slice(1, textHistory.length)
          .map((entry: ChatBoxType, i, arr) => {
            const isActiveStream = entry.client_id === LIVE_ID && streamActive;
            if (entry.client_id !== LIVE_ID && !entry.content && (!entry.guidelines || entry.guidelines.length === 0) && (!entry.guidelineDocs || entry.guidelineDocs.length === 0) && (!entry.memoryFacts || entry.memoryFacts.length === 0)) return null;
            // Break inline numbered-list items onto their own lines without
            // mangling dates/decimals — see normalizeListMarkers for the why.
            const text = normalizeListMarkers(entry.content);
            const guidelines = entry.guidelines;
            const guidelineDocs = entry.guidelineDocs;
            let content;
            if (isActiveStream && !text) {
              if (stream.isSummarizing) {
                content = (
                  <WaveText>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {t('chat.summarizing')}
                    </Typography>
                  </WaveText>
                );
              } else if (stream.isThinking) {
                content = <ThinkingIndicator />;
              } else if (stream.isRecallingMemory) {
                content = (
                  <WaveText>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      {t('chat.recallingMemory')}
                    </Typography>
                  </WaveText>
                );
              } else {
                content = (
                  <Skeleton
                    sx={{ bgcolor: "grey.800", marginTop: 0.5 }}
                    variant="circular"
                    width="15px"
                    height="15px"
                  />
                );
              }
            } else {
              content = entry.role === "system" ? (
                isActiveStream ? (
                  <TypewriterMarkdown text={text} cps={120} />
                ) : (
                  <MarkdownContent content={text} />
                )
              ) : (
                <S.StyledTypography sx={{ whiteSpace: "pre-wrap" }} variant="body2">{text}</S.StyledTypography>
              );
            }

            const node = (
              <ChatBox
                key={entry.client_id ?? `${entry.role}-${i}`}
                user={entry.role}
                domId={entry.messageId ? `msg-${entry.messageId}` : undefined}
                guidelines={guidelines}
                guidelineDocs={guidelineDocs}
                guidelineChunkIds={entry.guidelineChunkIds}
                memoryFacts={entry.memoryFacts}
                createdAt={entry.createdAt}
                ref={i === arr.length - 1 ? chatRef : undefined}
                isLoading={i === arr.length - 1 ? (entry.client_id === LIVE_ID && streamActive && !text) : undefined}
              >
                {content}
              </ChatBox>
            );
            return node;
          }).filter(Boolean)}
        {error !== "" && (
          <S.StyledAlert severity="error">
            {error}
          </S.StyledAlert>
        )}
        <div ref={chatRef}></div>
      </S.ChatWrapper>

      <S.ChatInputWrapper>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        {props.owner ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              mb: 0.75,
              borderRadius: 2,
              bgcolor: "action.hover",
              color: "text.secondary",
            }}
          >
            <Avatar
              src={props.owner.avatarUrl ?? undefined}
              sx={{ width: 22, height: 22, fontSize: 12 }}
            >
              {(props.owner.name || props.owner.email || "?").trim().charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {t("chat.sharedBy", {
                name: props.owner.name || props.owner.email || t("chat.anotherUser"),
              })}
            </Typography>
            {props.readOnly ? (
              <Typography variant="caption" sx={{ ml: "auto", opacity: 0.85 }}>
                {t("chat.viewOnly")}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {!props.readOnly ?
        <ChatInput
          conversationId={selectedUuid}
          isNewConversation={textHistory.length < 2}
          loading={isBusy}
          handleCancel={() => {
            // Interrupts + cancels the request. The controller finalizes the
            // stream (hasEnded); the end effect refetches the persisted partial.
            if (selectedUuid) provider.stopStream(selectedUuid);
          }}
          handleSubmit={async (event) => {
            if (props.readOnly) return;
            const entry = event;
            const { content, reference_ids } = entry;
            const trimmedContent = (content || "").trim();
            if (!trimmedContent) return;
            let activeConversationId = selectedUuid;
            if (
              props.pendingConversationId &&
              props.pendingConversationId === selectedUuid &&
              props.onCreateConversation
            ) {
              const created = await props.onCreateConversation();
              if (!created?.conversation_id) return;
              activeConversationId = created.conversation_id;
              setSelectedUuid(activeConversationId);
            }
            if (!activeConversationId) return;
            setError("");
            const userEntry: ChatBoxType = {
              role: "user",
              content: trimmedContent,
              reference_ids: reference_ids || {},
              createdAt: new Date().toISOString(),
            };
            // Optimistically show the user's message immediately (survives when
            // no remount happens; a brand-new conversation recovers it via the
            // post-persist refetch).
            setFetchedHistory((prev) => [...prev, userEntry]);
            try {
              await createMessage({
                conversation_id: activeConversationId,
                role: "user",
                content: trimmedContent,
                kind: "text",
              });
              provider.startStream(activeConversationId, {
                text: trimmedContent,
                patientId: derivedPatientId,
                runId: props.modelRunId,
                model: props.copilotModel,
                onSummary: (summary) =>
                  conversationSummaryRef.current?.(activeConversationId, summary),
              });
            } catch (err) {
              console.error("Failed to persist patient message:", err);
              setError("Failed to send message. Please try again.");
              setFetchedHistory((prev) => prev.filter((m) => m !== userEntry));
            }
          }}
        /> : null }
        </Box>
      </S.ChatInputWrapper>
    </S.Main>
  );
}
